import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from 'react-router-dom';
import {
    ReactFlow,
    Controls,
    Background,
    applyNodeChanges,
    applyEdgeChanges,
    useReactFlow,
    FitView
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomMindmapNode from '../components/Mindmap/CustomMindmapNode';
import ServiceGridNode from '../components/Mindmap/ServiceGridNode';

import Header from '../components/Header';
import { calculateLayout, createEdges, recentArticles, categoryColors, directionMap } from '../utils/layout-helper';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from 'react-helmet-async';

import servicesDataRaw from '../data/services.json';
import TagButton from '../components/TagButton';
const servicesData = servicesDataRaw;

const nodeTypes = {
    mindmap: CustomMindmapNode,
    serviceGrid: ServiceGridNode,
};

// [FEEDBACK] Super Category Mapping (Hierarchical Grouping)
// Mapping specific categories to broader "Super Categories" based on the Mindmap quadrants
const superCategoryMap = {
    "Ad & Marketing Automation": "광고 & 마케팅 자동화",
    "AI Development Platform": "AI 개발 플랫폼",
    "AI Search": "AI 검색",
    "Code Generation & Review": "코드 생성 & 리뷰",
    "Conversational AI": "대화형 AI",
    "Creative Design": "크리에이티브 디자인",
    "Customer Service Chatbot": "고객 서비스 챗봇",
    "Data Analysis & Prediction": "데이터 분석 & 예측",
    "Document & Content Creation": "문서 & 콘텐츠 생성",
    "Image & Video Analysis": "이미지 & 비디오 분석",
    "Image Generation & Editing": "이미지 생성 & 편집",
    "Legal & Security": "법률 & 보안",
    "Market & Customer Analysis": "시장 & 고객 분석",
    "Multimodal AI": "멀티모달 AI",
    "Music & Audio Generation": "음악 & 오디오 생성",
    "Presentation & Meeting": "프레젠테이션 & 회의",
    "Service & Health": "서비스 & 건강",
    "Video Generation & Editing": "비디오 생성 & 편집",
    "Workflow Automation": "워크플로우 자동화",
    "Writing Assistance": "작문 보조"
};

// Define the order/grouping of Super Categories
const superGroups = {
    "Creative & Entertainment": [
        "Image Generation & Editing",
        "Video Generation & Editing",
        "Music & Audio Generation",
        "Creative Design",
        "Writing Assistance",
        "Conversational AI"
    ],
    "Development & Data": [
        "AI Development Platform",
        "Code Generation & Review",
        "Data Analysis & Prediction",
        "AI Search",
        "Image & Video Analysis",
        "Multimodal AI"
    ],
    "Business & Productivity": [
        "Ad & Marketing Automation",
        "Workflow Automation",
        "Customer Service Chatbot",
        "Market & Customer Analysis",
        "Presentation & Meeting",
        "Document & Content Creation",
        "Legal & Security"
    ],
    "Education & General": [
        "Service & Health"
    ]
};

function MindmapPage() {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedNodeData, setSelectedNodeData] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [viewMode, setViewMode] = useState('mindmap'); // 'mindmap' or 'grid'
    const [activeSecondLevelNodeId, setActiveSecondLevelNodeId] = useState(null); // [FEEDBACK] Track active G2 node

    const [searchQuery, setSearchQuery] = useState('');

    const { fitView, getNodes, setCenter, setZoom, fitBounds } = useReactFlow();

    const { allNodes, allEdges, parentMap } = useMemo(() => {
        const calculatedNodes = calculateLayout();
        const initialEdges = createEdges();
        const pMap = new Map();
        initialEdges.forEach(edge => pMap.set(edge.target, edge.source));
        return { allNodes: calculatedNodes, allEdges: initialEdges, parentMap: pMap };
    }, []);

    const { theme } = useTheme();

    // 요약 텍스트를 50자로 제한하는 함수
    const truncateSummary = (text) => {
        if (!text) return '';
        if (text.length <= 50) return text;
        return text.substring(0, 100) + '...';
    };

    const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

    // [FEEDBACK] Interaction: Click G2 to show Grid, Click others to close
    const onNodeClick = useCallback((event, node) => {
        if (node.data.group === 2) {
            // Toggle if same node clicked, or switch to new one
            setActiveSecondLevelNodeId(prev => prev === node.id ? null : node.id);
            setSelectedNodeData(null); // Close detail panel when switching categories
        } else if (node.data.group === 0 || node.data.group === 1) {
            // Close grid if clicking root or level 1
            setActiveSecondLevelNodeId(null);
            setSelectedNodeData(null); // Close detail panel
        }
    }, []);

    const onPaneClick = useCallback(() => {
        // Close grid on background click
        setActiveSecondLevelNodeId(null);
        setSelectedNodeData(null); // Close detail panel
    }, []);

    /// 아티클 관련 -------------------------------------------
    const [currentArticleIndex, setCurrentArticleIndex] = useState(0);

    const goToNextArticle = useCallback(() => {
        setCurrentArticleIndex(prevIndex =>
            prevIndex === recentArticles.length - 1 ? 0 : prevIndex + 1
        );
    }, []);

    const goToPrevArticle = () => {
        setCurrentArticleIndex(prevIndex =>
            prevIndex === 0 ? recentArticles.length - 1 : prevIndex - 1
        );
    };

    useEffect(() => {
        const timer = setInterval(goToNextArticle, 10000); // 10초
        return () => clearInterval(timer);
    }, [currentArticleIndex, goToNextArticle]);
    ///-----------------------------------------------------------------

    const handleCloseGrid = useCallback(() => {
        setActiveSecondLevelNodeId(null);
    }, []);

    const handleShowDetails = useCallback((serviceData) => {
        const matched = servicesData.find(s => s.id === serviceData.id);
        if (matched) {
            setSelectedNodeData({
                ...matched,
                logoUrl: `/Images/Logos/${matched.id.replace(/ /g, '')}.png`,
            });
        } else {
            setSelectedNodeData({
                id: serviceData.id,
                label: serviceData.label,
                summary: serviceData.summary || '',
                features: [],
                tags: [],
                link: '',
                logoUrl: `/Images/Logos/${serviceData.id.replace(/ /g, '')}.png`,
            });
        }
    }, [setSelectedNodeData]);

    const navigate = useNavigate();

    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
    }, [theme]);

    // [FEEDBACK] 초기 로드 시에만 중앙 정렬 (한 번만 실행)
    useEffect(() => {
        const timer = setTimeout(() => {
            fitView({ padding: 0.3, duration: 800 });
        }, 500);
        return () => clearTimeout(timer);
    }, [fitView]);

    // 노드 및 엣지 상태 업데이트 (Filtering based on activeSecondLevelNodeId)
    useEffect(() => {
        let finalNodes = allNodes;
        const lowerCaseQuery = searchQuery.toLowerCase().trim();

        // Filter nodes
        finalNodes = finalNodes.filter(node => {
            // Always show G0, G1, G2
            if (node.data.group !== undefined && node.data.group < 3) return true;
            if (node.type !== 'serviceGrid') return true; // Safety check

            // Show Grid only if parent is active
            // Grid ID is `grid-{parentId}`
            const parentId = node.id.replace('grid-', '');
            return parentId === activeSecondLevelNodeId;
        });

        finalNodes = finalNodes.map(node => {
            const labelText = typeof node.data.label === 'string' ? node.data.label : '';
            const isHighlighted = lowerCaseQuery && labelText.toLowerCase().includes(lowerCaseQuery);

            // Highlight active G2 node
            const isActive = node.id === activeSecondLevelNodeId;

            if (node.type === 'serviceGrid') {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        isHighlighted,
                        theme,
                        onClose: handleCloseGrid,
                        onShowDetails: handleShowDetails
                    }
                };
            }

            // Add active state to G2 nodes
            let nodeData = { ...node.data, isHighlighted, theme: theme };
            if (node.data.group === 2 && isActive) {
                nodeData = { ...nodeData, isHighlighted: true }; // Reuse highlight style or add specific active style
            }

            return { ...node, data: nodeData };
        });

        setNodes(finalNodes);

        // Filter edges
        const themedEdges = allEdges.filter(edge => {
            const targetNode = allNodes.find(n => n.id === edge.target);
            if (!targetNode) return false;

            // If target is Grid, check visibility
            if (targetNode.type === 'serviceGrid') {
                const parentId = targetNode.id.replace('grid-', '');
                return parentId === activeSecondLevelNodeId;
            }
            return true;
        }).map(edge => ({
            ...edge,
            style: {
                ...edge.style,
                // Keep the color defined in layout-helper, or override here if needed
                // layout-helper already sets brighter colors
            }
        }));
        setEdges(themedEdges);
    }, [searchQuery, allNodes, allEdges, theme, handleCloseGrid, handleShowDetails, activeSecondLevelNodeId]);

    // [FEEDBACK] Grid View Hierarchical Grouping
    const groupedServices = useMemo(() => {
        const groups = {};

        // Initialize groups based on superGroups
        Object.keys(superGroups).forEach(superKey => {
            groups[superKey] = {};
            superGroups[superKey].forEach(subKey => {
                groups[superKey][subKey] = [];
            });
        });

        // Assign services to groups
        servicesData.forEach(service => {
            const category = service.category || 'Miscellaneous';
            let placed = false;

            // Find which Super Group this category belongs to
            for (const [superKey, subKeys] of Object.entries(superGroups)) {
                if (subKeys.includes(category)) {
                    // Filter logic
                    if (!searchQuery) {
                        groups[superKey][category].push(service);
                    } else {
                        const q = searchQuery.toLowerCase();
                        if (
                            service.name.toLowerCase().includes(q) ||
                            (service.summary && service.summary.toLowerCase().includes(q)) ||
                            (service.tags && service.tags.some(t => t.toLowerCase().includes(q)))
                        ) {
                            groups[superKey][category].push(service);
                        }
                    }
                    placed = true;
                    break;
                }
            }
        });

        // Remove empty groups
        Object.keys(groups).forEach(superKey => {
            let hasContent = false;
            Object.keys(groups[superKey]).forEach(subKey => {
                if (groups[superKey][subKey].length === 0) {
                    delete groups[superKey][subKey];
                } else {
                    hasContent = true;
                }
            });
            if (!hasContent) delete groups[superKey];
        });

        return groups;
    }, [searchQuery]);

    const superCategories = Object.keys(groupedServices);

    const scrollToCategory = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // [FEEDBACK] Quick Navigation Logic with Bounding Box Fit
    const handleNavigateToNode = useCallback((nodeId) => {
        if (nodeId === 'AI') {
            // Main Button: Show entire mindmap with better padding
            fitView({ padding: 0.3, duration: 1000 });
            return;
        }

        // Find the clicked category node
        const clickedNode = nodes.find(n => n.id === nodeId);
        if (!clickedNode) return;

        // Find all child nodes (G2 - subcategories)
        const childNodeIds = allEdges
            .filter(e => e.source === nodeId)
            .map(e => e.target);

        const childNodes = nodes.filter(n => childNodeIds.includes(n.id));

        // Find all grandchild nodes (G3 - service grids)
        const grandchildNodeIds = allEdges
            .filter(e => childNodeIds.includes(e.source))
            .map(e => e.target);

        const grandchildNodes = nodes.filter(n => grandchildNodeIds.includes(n.id));

        // Combine all relevant nodes for bounding box calculation
        const allRelevantNodes = [clickedNode, ...childNodes, ...grandchildNodes];

        if (allRelevantNodes.length === 0) return;

        // Calculate bounding box
        const positions = allRelevantNodes.map(n => n.position);
        const minX = Math.min(...positions.map(p => p.x));
        const maxX = Math.max(...positions.map(p => p.x));
        const minY = Math.min(...positions.map(p => p.y));
        const maxY = Math.max(...positions.map(p => p.y));

        // Add padding to account for node sizes
        const padding = 400;

        // Create bounding box
        const bounds = {
            x: minX - padding,
            y: minY - padding,
            width: (maxX - minX) + padding * 2,
            height: (maxY - minY) + padding * 2
        };

        // Fit view to the bounding box
        fitBounds(bounds, { padding: 0.05, duration: 1000 });
    }, [nodes, allEdges, fitView, fitBounds]);

    return (
        <>
            <Helmet>
                <title>AI 사이트 허브 – 생성형 AI 툴 비교·마인드맵 탐색</title>
                <meta name="description" content="카테고리별 인터랙티브 마인드맵으로 생성형 AI 서비스를 직관적으로 탐색하고, 가격·특징을 한눈에 비교하세요." />
            </Helmet>

            <div className="flex flex-col h-screen bg-gray-50 dark:bg-slate-900">
                <Header
                    onSearchChange={setSearchQuery}
                    fitView={fitView}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    showFitViewButton={viewMode === 'mindmap'}
                />

                <div className="flex flex-row flex-grow overflow-hidden relative">



                    {/* 사이드바 열기 버튼 */}
                    {!isSidebarOpen && (
                        <button onClick={() => setIsSidebarOpen(true)} className="absolute top-4 left-4 z-30 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all" aria-label="Open sidebar">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                        </button>
                    )}

                    {/* 좌측 사이드바 (뉴스 & 광고) - 항상 표시 (모바일에서는 토글) */}
                    {isSidebarOpen && (
                        <aside className={`bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-r dark:border-gray-700 flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${isSidebarOpen ? 'w-80' : 'w-0'} flex flex-col z-20`}>
                            <div className="p-6 border-b dark:border-gray-700 flex-shrink-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 whitespace-nowrap">
                                        📰 AI 최신 소식
                                    </h3>
                                    <button onClick={() => setIsSidebarOpen(false)} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            </div>

                            <div className="relative h-64 flex-shrink-0 bg-gray-50/50 dark:bg-gray-800/30">
                                <div className="overflow-hidden h-full">
                                    <div
                                        className="flex h-full transition-transform duration-500 ease-in-out"
                                        style={{ transform: `translateX(-${currentArticleIndex * 100}%)` }}
                                    >
                                        {recentArticles.map(article => (
                                            <div key={article.id} className="w-full h-full flex-shrink-0 p-6">
                                                <Link to={`/content/${article.contentId}`} className="block bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-lg h-full flex flex-col justify-center hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border border-gray-100 dark:border-gray-700 group">
                                                    <h4 className="font-bold text-lg text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 mb-2">{article.title}</h4>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed">{truncateSummary(article.summary)}</p>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button onClick={goToPrevArticle} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 dark:bg-black/60 backdrop-blur-sm rounded-full shadow-md hover:bg-white dark:hover:bg-black transition-all text-gray-700 dark:text-gray-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button onClick={goToNextArticle} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 dark:bg-black/60 backdrop-blur-sm rounded-full shadow-md hover:bg-white dark:hover:bg-black transition-all text-gray-700 dark:text-gray-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>

                            <div className="p-6 flex-1 border-t dark:border-gray-700 flex flex-col bg-white/30 dark:bg-gray-900/30 backdrop-blur-sm">
                                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl flex-1 flex flex-col items-center justify-center bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <span className="text-gray-400 dark:text-gray-500 text-sm font-medium z-10">광고 영역 (300x250)</span>
                                    <span className="text-xs text-gray-300 dark:text-gray-600 mt-2 z-10">Google Adsense</span>
                                </div>
                            </div>
                            <div className="mt-auto p-6 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
                                <div className="font-bold text-gray-900 dark:text-gray-200 mb-2 text-sm">With-AI</div>
                                <div className="space-y-1 opacity-80">
                                    <div>상호명: Green Apple 이정현</div>
                                    <div>사업자등록번호: 895-07-03078</div>
                                    <div>© 2025 Green Apple. All rights reserved.</div>
                                </div>
                                <div className="mt-3 flex gap-3">
                                    <a href="#" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">개인정보 처리방침</a>
                                    <span className="text-gray-300">|</span>
                                    <a href="#" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">이용약관</a>
                                </div>
                            </div>
                        </aside>
                    )}


                    <main className="flex-grow relative h-full overflow-hidden flex">
                        {viewMode === 'mindmap' ? (
                            <>
                                <div className="flex-grow h-full relative">
                                    <ReactFlow {...{ nodes, edges, onNodesChange, onEdgesChange, onNodeClick, nodeTypes }}
                                        onPaneClick={onPaneClick}
                                        nodesDraggable={false}
                                        panOnDrag={true}
                                        zoomOnScroll={true}
                                        zoomOnPinch={true}
                                        elementsSelectable={true}
                                        minZoom={0.1}
                                        maxZoom={2.0}
                                        fitView
                                        fitViewOptions={{ padding: 0.2, duration: 1000 }}
                                        defaultViewport={undefined} // Let fitView handle it
                                    >
                                        <Background variant="dots" gap={20} size={1} color={theme === 'light' ? '#e0e0e0' : '#4a5568'} />
                                        <Controls showInteractive={false} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm" />
                                    </ReactFlow>
                                </div>

                                {/* [FEEDBACK] Quick Navigation Sidebar (Bottom Center - Horizontal) */}
                                {/* Moved inside main to align with mindmap center */}
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex flex-col gap-2">
                                    {/* Row 1: Main + 4 categories */}
                                    <div className="flex gap-2 justify-center">
                                        {/* Root Node Button */}
                                        <button
                                            onClick={() => handleNavigateToNode('AI')}
                                            className="group flex items-center gap-2 px-3 py-2 rounded-full shadow-md bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-blue-200 dark:border-blue-700 hover:scale-105 transition-all duration-200"
                                            title="Main"
                                        >
                                            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600" />
                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap">Main</span>
                                        </button>

                                        {/* First 4 categories */}
                                        {Object.keys(directionMap).slice(0, 4).map((catId) => {
                                            const colorClass = categoryColors[catId] || 'from-gray-500 to-gray-600';
                                            return (
                                                <button
                                                    key={catId}
                                                    onClick={() => handleNavigateToNode(catId)}
                                                    className="group flex items-center gap-2 px-3 py-2 rounded-full shadow-md bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 hover:scale-105 transition-all duration-200"
                                                    title={catId}
                                                >
                                                    <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${colorClass}`} />
                                                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap">{catId}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Row 2: Remaining 4 categories */}
                                    <div className="flex gap-2 justify-center">
                                        {Object.keys(directionMap).slice(4).map((catId) => {
                                            const colorClass = categoryColors[catId] || 'from-gray-500 to-gray-600';
                                            return (
                                                <button
                                                    key={catId}
                                                    onClick={() => handleNavigateToNode(catId)}
                                                    className="group flex items-center gap-2 px-3 py-2 rounded-full shadow-md bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 hover:scale-105 transition-all duration-200"
                                                    title={catId}
                                                >
                                                    <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${colorClass}`} />
                                                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200 whitespace-nowrap">{catId}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* 우측 상세정보 패널 (마인드맵 모드) */}
                                <div className={`absolute top-4 right-4 bottom-4 w-80 z-20 transform transition-transform duration-300 ease-in-out ${selectedNodeData ? 'translate-x-0' : 'translate-x-[120%]'}`}>
                                    <div className="h-full flex flex-col bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 dark:border-gray-700 shadow-2xl rounded-3xl overflow-hidden">
                                        {selectedNodeData ? (
                                            <div className="flex flex-col h-full relative">
                                                {/* Header with Gradient Background */}
                                                <div className="relative h-32 bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                                                    <button
                                                        onClick={() => setSelectedNodeData(null)}
                                                        className="absolute top-4 right-4 p-2 bg-white/50 dark:bg-black/20 rounded-full hover:bg-white/80 dark:hover:bg-black/40 transition-colors z-10"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>

                                                    <div className="absolute -bottom-10 p-2 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                                                        <img
                                                            key={selectedNodeData.id}
                                                            src={selectedNodeData.logoUrl && selectedNodeData.logoUrl.trim() !== '' ? selectedNodeData.logoUrl : '/Images/Logos/Base.jpg'}
                                                            alt={`${selectedNodeData.name || selectedNodeData.id} 로고`}
                                                            className="w-20 h-20 object-contain rounded-xl"
                                                            onError={e => { e.target.onerror = null; e.target.src = '/Images/Logos/Base.jpg'; }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 overflow-y-auto pt-12 px-6 pb-6 custom-scrollbar">
                                                    <div className="text-center mb-6">
                                                        <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300">
                                                            {selectedNodeData.name || selectedNodeData.id}
                                                        </h2>
                                                        {selectedNodeData.tags && selectedNodeData.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 justify-center mt-3">
                                                                {selectedNodeData.tags.slice(0, 3).map((tag, i) => (
                                                                    <span key={i} className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-md">
                                                                        #{tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="space-y-6">
                                                        {selectedNodeData.summary && (
                                                            <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                                                                <h3 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Summary</h3>
                                                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                                                                    {selectedNodeData.summary}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {selectedNodeData.features && selectedNodeData.features.length > 0 && (
                                                            <div>
                                                                <h3 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Key Features</h3>
                                                                <ul className="space-y-2">
                                                                    {selectedNodeData.features.map((f, i) => (
                                                                        <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                                                                            <span className="mr-2 text-green-500 mt-1">✓</span>
                                                                            {f}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Footer Actions */}
                                                <div className="p-6 bg-gray-50 dark:bg-slate-800/80 border-t border-gray-100 dark:border-gray-700 flex gap-3 flex-shrink-0">
                                                    {selectedNodeData.link && (
                                                        <a
                                                            href={selectedNodeData.link}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="flex-1 flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-500/30 text-sm"
                                                        >
                                                            사이트 방문
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                        </a>
                                                    )}
                                                    {selectedNodeData.id && (
                                                        <Link
                                                            to={`/details/${selectedNodeData.id}`}
                                                            className="flex-1 flex items-center justify-center bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-800 dark:text-white font-bold py-3 px-4 rounded-xl transition-all border border-gray-200 dark:border-gray-600 shadow-sm text-sm"
                                                        >
                                                            상세 정보
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 p-8 text-center">
                                                <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                </div>
                                                <p>서비스를 선택하면<br />상세 정보가 표시됩니다.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* [FEEDBACK] 그리드 뷰 모드 개선: 계층적 그룹화 및 우측 사이드바 */
                            <div className="flex w-full h-full bg-gray-50 dark:bg-slate-900">

                                {/* 메인 그리드 영역 (좌측으로 이동) */}
                                <div className="flex-1 overflow-y-auto py-8 px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 scroll-smooth custom-scrollbar">
                                    <div className="max-w-8xl mx-auto space-y-16">
                                        {superCategories.map(superCat => (
                                            <div key={superCat} id={`super-${superCat}`} className="scroll-mt-8">
                                                <div className="flex items-center mb-8 border-b border-gray-200 dark:border-gray-700 pb-4">
                                                    <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                                                        {superCat}
                                                    </h2>
                                                </div>

                                                <div className="space-y-12">
                                                    {Object.keys(groupedServices[superCat]).map(subCat => (
                                                        <div key={subCat} id={`sub-${subCat}`} className="scroll-mt-8 pl-4 border-l-4 border-blue-500 dark:border-blue-600">
                                                            <h3 className="text-xl font-bold text-gray-700 dark:text-gray-200 mb-6 flex items-center">
                                                                {superCategoryMap[subCat] || subCat}
                                                            </h3>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                                                                {groupedServices[superCat][subCat].map(service => (
                                                                    <div key={service.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 flex flex-col group min-h-[420px]">
                                                                        <div className="p-6 flex flex-col flex-grow">
                                                                            <div className="flex items-center justify-center mb-4 h-20 group-hover:scale-105 transition-transform duration-300">
                                                                                <img
                                                                                    src={`/Images/Logos/${service.id.replace(/ /g, '')}.png`}
                                                                                    alt={service.name}
                                                                                    className="h-16 object-contain"
                                                                                    onError={e => { e.target.onerror = null; e.target.src = '/Images/Logos/Base.jpg'; }}
                                                                                />
                                                                            </div>
                                                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">{service.name}</h3>
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400 text-center line-clamp-3 mb-2 flex-grow">
                                                                                {service.summary || service.introduction}
                                                                            </p>
                                                                            <div className="flex flex-wrap gap-2 justify-center mb-2">
                                                                                {(service.tags || []).slice(0, 3).map((tag, i) => (
                                                                                    <span key={i} className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full">
                                                                                        #{tag}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                            <Link
                                                                                to={`/details/${service.id}`}
                                                                                className="block w-full text-center bg-gray-100 dark:bg-gray-700 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-600 text-gray-700 dark:text-gray-200 font-semibold py-2 px-4 rounded-lg transition-colors"
                                                                            >
                                                                                상세보기
                                                                            </Link>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* [FEEDBACK] 그리드 뷰 전용 TOC 사이드바 (우측으로 이동) */}
                                <div className="w-72 bg-white dark:bg-gray-800 border-l dark:border-gray-700 overflow-y-auto hidden lg:block p-6 shadow-xl z-20 custom-scrollbar">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 px-2 border-b pb-2 dark:border-gray-700">카테고리</h3>
                                    <nav className="space-y-6">
                                        {superCategories.map(superCat => (
                                            <div key={superCat}>
                                                <button
                                                    onClick={() => scrollToCategory(`super-${superCat}`)}
                                                    className="w-full text-left px-2 py-1 text-base font-bold text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-2"
                                                >
                                                    {superCat}
                                                </button>
                                                <div className="space-y-1 pl-4 border-l border-gray-200 dark:border-gray-700 ml-2">
                                                    {Object.keys(groupedServices[superCat]).map(subCat => (
                                                        <button
                                                            key={subCat}
                                                            onClick={() => scrollToCategory(`sub-${subCat}`)}
                                                            className="w-full text-left px-2 py-1 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded transition-colors"
                                                        >
                                                            {superCategoryMap[subCat] || subCat}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </nav>
                                </div>

                            </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}

export default MindmapPage;
